import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { fmtDateTime } from "./adminUtils";

export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/admin/users/${userId}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <p className="font-mono text-sm text-muted-foreground">Loading…</p>;
  if (!data) return <p className="font-mono text-sm text-muted-foreground">User not found.</p>;

  return (
    <div className="space-y-6" data-testid="admin-user-detail-page">
      <Button variant="ghost" size="sm" className="rounded-sm -ml-2" onClick={() => navigate("/admin/users")} data-testid="admin-user-back">
        <ArrowLeft size={16} className="mr-2" /> All users
      </Button>

      <PageHeader title={data.name || data.email} subtitle="User details across all organizations." />

      <section className="bg-card border border-border rounded-sm p-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><p className="text-muted-foreground text-xs uppercase">Email</p><p className="font-mono text-xs break-all">{data.email}</p></div>
          <div><p className="text-muted-foreground text-xs uppercase">Platform Admin</p>{data.is_platform_admin ? <Badge className="rounded-sm text-[10px] uppercase">Yes</Badge> : <span className="text-muted-foreground">No</span>}</div>
          <div><p className="text-muted-foreground text-xs uppercase">Auth Provider</p><p className="capitalize">{data.auth_provider}</p></div>
          <div><p className="text-muted-foreground text-xs uppercase">Last Login</p><p>{fmtDateTime(data.last_login)}</p></div>
        </div>
      </section>

      <section className="bg-card border border-border rounded-sm p-5 space-y-4" data-testid="admin-user-orgs">
        <h3 className="font-heading text-lg">Organizations ({data.organizations.length})</h3>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Organization</TableHead><TableHead>Slug</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {data.organizations.map((o) => (
                <TableRow key={o.org_id} className="cursor-pointer" onClick={() => navigate(`/admin/organizations/${o.org_id}`)} data-testid={`admin-user-org-${o.org_id}`}>
                  <TableCell className="font-medium">{o.org_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{o.slug || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm text-[10px] uppercase">{o.role}</Badge></TableCell>
                  <TableCell><Badge variant={o.status === "active" ? "default" : "secondary"} className="rounded-sm text-[10px] uppercase">{o.status}</Badge></TableCell>
                  <TableCell className="text-right text-muted-foreground text-xs">view →</TableCell>
                </TableRow>
              ))}
              {data.organizations.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-4">No organizations</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}
